import { useCallback, useEffect, useState } from 'react';
import apiCoreAdapter from './apiCoreAdapter';
import type { Session } from '../types/session';
import {
  calculateNZSettlementSchedule,
  MonthlySettledSessions,
  NZSettlementMonthResult,
  NZ_SETTLEMENT_SCHEME_START_MONTH,
} from '../utils/nzLiquidacion';

export interface UseNZSettlementScheduleResult {
  schedule: NZSettlementMonthResult[];
  /** Las mismas sesiones settled ya traídas para calcular schedule, agrupadas por mes
   *  calendario ('YYYY-MM') y ordenadas por fecha ascendente — para mostrar el detalle de
   *  qué sesiones componen cada mes/acumulado sin pegarle de nuevo a la API. */
  sessionsByMonth: Record<string, Session[]>;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

const SESSIONS_FETCH_TAKE = 5000; // mismo orden de magnitud que MetricsPsiPlusTab para rangos multi-mes

function formatDateForApi(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function monthKeyFromSessionDate(sessionDate: string): string {
  // Session.date viene como 'YYYY-MM-DD HH:mm:ss' (ver src/types/session.ts).
  return sessionDate.slice(0, 7);
}

/** Todas las claves 'YYYY-MM' entre startMonth y endMonth, ambos inclusive. */
function enumerateMonths(startMonth: string, endMonth: string): string[] {
  const [startYear, startMonthNum] = startMonth.split('-').map(Number);
  const [endYear, endMonthNum] = endMonth.split('-').map(Number);
  const months: string[] = [];
  let year = startYear;
  let month = startMonthNum;
  while (year < endYear || (year === endYear && month <= endMonthNum)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

/**
 * Conecta calculateNZSettlementSchedule (cálculo puro, sin persistencia) a sesiones settled
 * reales: trae todas las sesiones settled desde NZ_SETTLEMENT_SCHEME_START_MONTH hasta hoy con
 * apiCoreAdapter.psychologistSessionsList (que sí soporta from/to reales, a diferencia de la
 * llamada directa que usa MetricsLiquidacionTab para el detalle expandible), las agrupa por mes
 * calendario en el cliente (no hay ningún endpoint/hook que ya las devuelva agrupadas así — ver
 * docs/liquidacion-nz-research.md) y recalcula el schedule completo en cada carga.
 *
 * NO decide si la psicóloga es NZ: ese gate lo resuelve ProfileMetricsTab (journeyMode) y baja
 * por props hasta MetricsLiquidacionTab, que monta este hook solo cuando corresponde. Mirar
 * `rankingCategory` del contexto acá sería un segundo criterio en paralelo al central — el bug
 * que arregló el commit 4e380d1 — y además decidiría con `undefined` si el perfil todavía no
 * cargó (ver el comentario de useNzJourneyStatus sobre quién popula el contexto).
 */
export function useNZSettlementSchedule(): UseNZSettlementScheduleResult {
  const [schedule, setSchedule] = useState<NZSettlementMonthResult[]>([]);
  const [sessionsByMonth, setSessionsByMonth] = useState<Record<string, Session[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(false);
      try {
        const today = new Date();
        const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

        const resp = await apiCoreAdapter.psychologistSessionsList({
          from: `${NZ_SETTLEMENT_SCHEME_START_MONTH}-01 00:00:00`,
          to: formatDateForApi(today),
          take: SESSIONS_FETCH_TAKE,
          status: 'settled',
        });

        if (cancelled) return;

        const sessionsGroupedByMonth = new Map<string, Session[]>();
        for (const session of resp.data) {
          const monthKey = monthKeyFromSessionDate(session.date);
          const bucket = sessionsGroupedByMonth.get(monthKey);
          if (bucket) bucket.push(session);
          else sessionsGroupedByMonth.set(monthKey, [session]);
        }
        sessionsGroupedByMonth.forEach((bucket) => {
          bucket.sort((a, b) => a.date.localeCompare(b.date));
        });

        const months = enumerateMonths(NZ_SETTLEMENT_SCHEME_START_MONTH, currentMonth);
        const monthlySessions: MonthlySettledSessions[] = months.map((month) => ({
          month,
          sessionsCount: sessionsGroupedByMonth.get(month)?.length ?? 0,
        }));

        setSchedule(calculateNZSettlementSchedule(monthlySessions, today));
        setSessionsByMonth(Object.fromEntries(sessionsGroupedByMonth));
      } catch {
        if (!cancelled) setSchedule([]);
        if (!cancelled) setSessionsByMonth({});
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return { schedule, sessionsByMonth, loading, error, refetch };
}
